import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TableCell, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { convertVariantSearchItemToProductVariant } from '@/lib/product-utils';
import { ProductVariant, VariantStockSearchItem } from '@/types/product-type';
import { Minus, MoreHorizontal, Package, Pencil, Plus } from 'lucide-react';

interface VariantFlatRowProps {
  variant: VariantStockSearchItem;
  onEditVariant?: (variant: ProductVariant) => void;
  onAdjustIn?: (variant: VariantStockSearchItem) => void;
  onAdjustOut?: (variant: VariantStockSearchItem) => void;
}

export function VariantFlatRow({ variant, onEditVariant, onAdjustIn, onAdjustOut }: VariantFlatRowProps) {
  const stockInHand = variant.stockInHand ?? 0;
  const openingQty = variant.openQty ?? 0;
  const receiveQty = variant.receiveQty ?? 0;
  const adjustInQty = variant.adjustInQty ?? 0;
  const adjustOutQty = variant.adjustOutQty ?? 0;
  const soldQty = variant.soldQty ?? 0;
  const closingQty = variant.closingQty ?? 0;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(price);
  };

  return (
    <TableRow
      className={`
        transition-all duration-150
        border-b border-slate-200/40
        ${variant.isActive === false
          ? 'bg-slate-50 hover:bg-slate-100/50'
          : 'bg-white even:bg-slate-50/30 hover:bg-blue-50/40'
        }
        group
      `}
    >
      {/* Product Name Column */}
      <TableCell className="py-2 sm:py-3 px-2 min-w-0">
        <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
          <Avatar className="h-7 w-7 sm:h-8 sm:w-8 ring-1 ring-slate-100 shrink-0">
            <AvatarImage src={variant.productImageUrl || undefined} />
            <AvatarFallback className="bg-gradient-to-br from-blue-50 to-indigo-100 text-slate-600 font-semibold text-xs sm:text-sm">
              {(variant.productName ?? 'P').substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-slate-900 text-xs sm:text-sm leading-tight truncate flex items-center gap-2">
              <span className="truncate">{variant.productName || 'N/A'}</span>
            </div>
            <div className="mt-1">
              {variant.isActive === false ? (
                <span className="shrink-0 text-[10px] sm:text-xs px-1.5 py-0.5 rounded border border-slate-200 bg-slate-100 text-slate-500">
                  Inactive
                </span>
              ) : (
                <span className="shrink-0 text-[10px] sm:text-xs px-1.5 py-0.5 rounded border border-green-200 bg-green-100 text-green-600">
                  Active
                </span>
              )}
            </div>
          </div>
        </div>
      </TableCell>

      {/* Variant Name / SKU Column */}
      <TableCell className="py-2 sm:py-3 px-2 min-w-0">
        <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
          <Avatar className="h-7 w-7 sm:h-8 sm:w-8 ring-1 ring-slate-100 shrink-0">
            <AvatarImage src={variant.imageUrl || undefined} />
            <AvatarFallback className="bg-gradient-to-br from-indigo-50 to-purple-100 text-slate-600 font-semibold text-xs sm:text-sm">
              {variant.variantName.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-slate-900 text-xs sm:text-sm leading-tight truncate">
              {variant.variantName}
            </div>
            <div className="flex items-center space-x-1 sm:space-x-1.5 min-w-0 mt-0.5">
              <Package className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-400 shrink-0" />
              <span className="text-xs sm:text-sm font-mono text-slate-500 truncate min-w-0">
                {variant.sku || 'N/A'}
              </span>
            </div>
            {variant.saleCode && (
              <div className="text-xs text-slate-400 mt-0.5 truncate">
                Code: {variant.saleCode}
              </div>
            )}
          </div>
        </div>
      </TableCell>

      {/* Stock Movement Column */}
      <TableCell className="py-2 sm:py-3 px-1.5 sm:px-2 text-center min-w-0">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="space-y-0.5 cursor-help min-w-0 max-w-full">
                {/* Stock in Hand */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-evenly space-y-0.5 sm:space-y-0 sm:space-x-1 min-w-0">
                  <div className="flex items-center justify-center sm:justify-end space-x-0.5 flex-shrink-0 flex-wrap gap-0.5">
                    <span className="text-xs sm:text-sm font-bold text-slate-900 whitespace-nowrap">
                      {stockInHand.toLocaleString()}
                    </span>
                    {(receiveQty > 0 || adjustInQty > 0) && (
                      <span className="inline-flex items-center px-1 sm:px-1.5 py-0.5 rounded text-xs sm:text-sm font-medium bg-green-100 text-green-700 whitespace-nowrap">
                        +{(receiveQty + adjustInQty).toLocaleString()}
                      </span>
                    )}
                    {adjustOutQty > 0 && (
                      <span className="inline-flex items-center px-1 sm:px-1.5 py-0.5 rounded text-xs sm:text-sm font-medium bg-red-100 text-red-700 whitespace-nowrap">
                        -{adjustOutQty.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Sold */}
                {soldQty > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-evenly space-y-0.5 sm:space-y-0 sm:space-x-1">
                    <div className="flex items-center justify-center sm:justify-start space-x-1 ">
                      <span className="text-xs sm:text-sm text-green-700 font-bold">Sold: </span>
                      <span className="text-xs sm:text-sm font-bold text-slate-900 whitespace-nowrap">
                        {soldQty.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm">
              <div className="space-y-2">
                <div className="font-semibold text-slate-900">Stock Movement</div>
                <div className="text-xs text-slate-600 space-y-1.5">
                  <div className="flex justify-between items-start">
                    <div>
                      <strong>Opening:</strong> Stock count at start of day
                    </div>
                    <span className="font-semibold text-slate-900 ml-2">
                      {openingQty.toLocaleString()}
                    </span>
                  </div>

                  {(receiveQty > 0 || adjustInQty > 0) && (
                    <div className="flex justify-between items-start">
                      <div>
                        <strong>Stock In:</strong> Received and adjusted in
                      </div>
                      <span className="font-semibold text-green-700 ml-2">
                        +{(receiveQty + adjustInQty).toLocaleString()}
                      </span>
                    </div>
                  )}

                  {adjustOutQty > 0 && (
                    <div className="flex justify-between items-start">
                      <div>
                        <strong>Adjust Out:</strong> Manual stock removals
                      </div>
                      <span className="font-semibold text-red-700 ml-2">
                        -{adjustOutQty.toLocaleString()}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-start pt-1 border-t border-slate-200">
                    <div>
                      <strong>Stock in Hand:</strong> Current available stock quantity
                    </div>
                    <span className="font-semibold text-blue-700 ml-2">
                      {stockInHand.toLocaleString()}
                    </span>
                  </div>

                  {soldQty > 0 && (
                    <div className="flex justify-between items-start">
                      <div>
                        <strong>Sold:</strong> Stock sold to customers
                      </div>
                      <span className="font-semibold text-slate-900 ml-2">
                        {soldQty.toLocaleString()}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-start pt-1 border-t border-slate-200">
                    <div>
                      <strong>Closing:</strong> Final stock count at end of day
                    </div>
                    <span className="font-semibold text-slate-900 ml-2">
                      {closingQty.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>

      {/* Price Column - Hidden on mobile */}
      <TableCell className="hidden sm:table-cell py-2 sm:py-3 px-2 text-right min-w-0">
        <span className="text-xs sm:text-sm font-bold text-green-700">
          {variant.price ? formatPrice(variant.price) : 'N/A'}
        </span>
      </TableCell>

      {/* Actions Column */}
      <TableCell className="py-2 sm:py-3 px-3 text-center" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 gap-1.5 border-slate-300 bg-white text-slate-600 hover:bg-slate-100 hover:border-slate-400 hover:text-slate-900 shadow-sm transition-all group-hover:border-blue-300 group-hover:text-blue-700"
            >
              <MoreHorizontal className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline text-xs font-medium">Actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEditVariant?.(convertVariantSearchItemToProductVariant(variant))}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            {onAdjustIn && (
              <DropdownMenuItem onClick={() => onAdjustIn(variant)} className="text-green-600 hover:text-green-700 hover:bg-green-50">
                <Plus className="mr-2 h-4 w-4" />
                Adjust In
              </DropdownMenuItem>
            )}
            {onAdjustOut && (
              <DropdownMenuItem onClick={() => onAdjustOut(variant)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Minus className="mr-2 h-4 w-4" />
                Adjust Out
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
